import clsx from "clsx";
import { useState } from "react";
import { useIntl } from "react-intl";

import { Button } from "@/components/Button/Button";
import { DrawerTopBar } from "@/components/Drawer/DrawerTopBar";
import { PersistentDrawer } from "@/components/Drawer/PersistentDrawer";
import { SwipeableDrawer } from "@/components/Drawer/SwipeableDrawer";
import { Radio } from "@/components/Radio/Radio";
import { RadioGroup } from "@/components/Radio/RadioGroup";
import type { MappedServiceData } from "@/features/flights/components/flight-purchase/AdditionalServicesSelection/types";
import { formatCurrency } from "@/utils/currency";

interface Props {
  open: boolean;
  onClose: () => void;
  onConfirm: (service: MappedServiceData | null, optionCode: string) => void;
  selectedOptionCode?: string;
  service: MappedServiceData | null;
  headerTitle: React.ReactNode;
}

export const MealSelectionDrawer = ({
  open,
  service,
  selectedOptionCode,
  headerTitle,
  onConfirm,
  onClose,
}: Props) => {
  const intl = useIntl();
  const [value, setValue] = useState(selectedOptionCode ?? "");

  return (
    <SwipeableDrawer
      open={open}
      onClose={onClose}
      backgroundColor="var(--color-surface-background)"
      hasFullHeight={true}
      heightOffset={96}
      fixedHeader={
        <DrawerTopBar
          onCloseClick={onClose}
          titleCentered={false}
          title={
            <div className="flex flex-col gap-1">
              <div className="text-end text-xl font-medium text-text-primary">
                {intl.formatMessage({
                  defaultMessage: "Meal selection",
                  description:
                    "Section heading for meal selection for a traveller",
                })}
              </div>
              {headerTitle}
            </div>
          }
        />
      }
    >
      <div className="mb-[calc(var(--drawer-height,0)-var(--drawer-bottom-padding,0))] flex flex-col rounded-3xl bg-white p-4">
        <RadioGroup
          row
          value={value}
          onChange={(e) => setValue(e.target.value)}
        >
          {service?.service.options.map((option, idx, arr) => {
            return (
              <div
                key={option.code}
                className={clsx("w-full", {
                  "pb-3": !service.mandatory || idx !== arr.length - 1,
                  "pt-3": idx !== 0,
                  "border-t border-stroke-neutral-soft": idx !== 0,
                })}
              >
                <Radio
                  value={option.code}
                  labelPlacement="start"
                  label={
                    <div className="flex flex-col items-end gap-1 text-end text-text-primary">
                      <div className="font-medium">{option.description}</div>
                      {option.price.amount !== null ? (
                        <div className="text-sm font-semibold">
                          {formatCurrency(
                            option.price.amount,
                            option.price.currency || undefined,
                          )}
                        </div>
                      ) : null}
                    </div>
                  }
                />
              </div>
            );
          })}
          {service?.mandatory ? null : (
            <div className="w-full border-t border-stroke-neutral-soft pt-3">
              <Radio
                value={""}
                labelPlacement="start"
                label={
                  <div className="flex flex-col items-end gap-1 text-end text-text-primary">
                    <div className="font-medium">
                      {intl.formatMessage({
                        defaultMessage: "No additional food",
                        description:
                          "Option label indicating no additional food or meal is selected",
                      })}
                    </div>
                    <div className="text-sm font-semibold">
                      {formatCurrency(0, "IQD")}
                    </div>
                  </div>
                }
              />
            </div>
          )}
        </RadioGroup>
      </div>
      <PersistentDrawer>
        <div className="mt-auto">
          <Button
            fullWidth
            variant="contained"
            size="medium"
            onClick={() => {
              onConfirm(service, value);
              onClose();
            }}
          >
            {intl.formatMessage({
              defaultMessage: "Confirm",
              description:
                "Generic 'Confirm' button label to submit or accept an action",
            })}
          </Button>
        </div>
      </PersistentDrawer>
    </SwipeableDrawer>
  );
};
